import { useState, useEffect } from "react";
import { api } from "../api.js";
import { card, labelStyle, inputStyle, selectStyle, btnPrimary, btnSecondary, SectionTitle, Badge } from "./ui.js";

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  emailVerified: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

interface Invitation {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  createdAt: string;
  inviteLink: string;
}

export default function UsersTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [justInvited, setJustInvited] = useState<{ email: string; inviteLink: string } | null>(null);

  const copyLink = async (id: string, link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
    } catch {
      // 古いブラウザ互換
      const ta = document.createElement("textarea");
      ta.value = link;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 2000);
    }
  };

  const load = async () => {
    try {
      const [u, i] = await Promise.all([api.getUsers(), api.getInvitations()]);
      setUsers(u.users || []);
      setInvitations(i.invitations || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleInvite = async () => {
    setSaving(true); setError(null);
    try {
      const res = await api.inviteUser({ email, role });
      setJustInvited({ email, inviteLink: res?.invitation?.inviteLink || "" });
      setEmail(""); setRole("member"); setShowInviteForm(false);
      await load();
    } catch (e: any) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleCancelInvite = async (id: string) => {
    if (!confirm("招待を取り消しますか？")) return;
    try {
      // 取り消す招待が直前送信バナーと同じなら非表示化
      const cancelling = invitations.find((i) => i.id === id);
      if (cancelling && justInvited && cancelling.email === justInvited.email) {
        setJustInvited(null);
      }
      await api.cancelInvitation(id);
      await load();
    } catch (e: any) { setError(e.message); }
  };

  const handleChangeRole = async (id: string, newRole: string) => {
    try { await api.updateUserRole(id, newRole); await load(); }
    catch (e: any) { setError(e.message); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("このユーザーを削除しますか？")) return;
    try { await api.deleteUser(id); await load(); }
    catch (e: any) { setError(e.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#6b6b68" }}>読み込み中...</div>;

  const roleBadge = (r: string) => {
    if (r === "owner") return <Badge bg="#EEEDFE" color="#534AB7">オーナー</Badge>;
    if (r === "admin") return <Badge bg="#E6F1FB" color="#185FA5">管理者</Badge>;
    return <Badge bg="#F0EFEB" color="#6b6b68">メンバー</Badge>;
  };

  return (
    <>
      <SectionTitle>ユーザー管理</SectionTitle>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 14, marginBottom: 16 }}>{error}</div>
      )}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>メンバー</div>
          <button onClick={() => setShowInviteForm(!showInviteForm)} style={{ ...btnPrimary, fontSize: 13 }}>
            {showInviteForm ? "キャンセル" : "+ メンバーを招待"}
          </button>
        </div>

        {users.length === 0 && !showInviteForm && (
          <div style={{ padding: "24px 0", textAlign: "center", color: "#6b6b68", fontSize: 15 }}>
            メンバーがいません。招待してください。
          </div>
        )}

        {users.map((u) => (
          <div key={u.id} style={{ padding: "14px 0", borderTop: "1px solid #f0efeb", display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{u.name || u.email}</div>
                {roleBadge(u.role)}
                {!u.emailVerified && <Badge bg="#FAEEDA" color="#854F0B">未検証</Badge>}
              </div>
              <div style={{ fontSize: 13, color: "#6b6b68" }}>{u.email}</div>
            </div>
            <select value={u.role} onChange={(e) => handleChangeRole(u.id, e.target.value)}
              style={{ ...selectStyle, width: 120, padding: "6px 10px", fontSize: 13 }} disabled={u.role === "owner"}>
              <option value="owner">オーナー</option>
              <option value="admin">管理者</option>
              <option value="member">メンバー</option>
            </select>
            <button onClick={() => handleDelete(u.id)} style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px", color: "#A32D2D", borderColor: "#f0c8c8" }}>
              削除
            </button>
          </div>
        ))}

        {showInviteForm && (
          <div style={{ borderTop: "1px solid #f0efeb", marginTop: 16, paddingTop: 20 }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 14 }}>メンバーを招待</div>
            <div style={{ marginBottom: 14 }}>
              <div style={labelStyle}>メールアドレス</div>
              <input style={inputStyle} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={labelStyle}>ロール</div>
              <select style={selectStyle} value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="member">メンバー（送信・閲覧のみ）</option>
                <option value="admin">管理者（ユーザー管理含む全操作）</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button style={{ ...btnPrimary, opacity: !email ? 0.5 : 1 }} onClick={handleInvite} disabled={saving || !email}>
                {saving ? "送信中..." : "招待メールを送信"}
              </button>
              <button style={btnSecondary} onClick={() => setShowInviteForm(false)}>キャンセル</button>
            </div>
          </div>
        )}
      </div>

      {/* 招待送信直後のバナー（リンクコピーできる） */}
      {justInvited && (
        <div style={{
          padding: "14px 16px", borderRadius: 10, background: "#E6F6EC", color: "#166534",
          fontSize: 14, marginBottom: 16, lineHeight: 1.7,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>✓ {justInvited.email} に招待メールを送信しました</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
            <input
              readOnly
              value={justInvited.inviteLink}
              style={{
                flex: 1, padding: "8px 10px", fontSize: 12, border: "1px solid #c8e6d3",
                borderRadius: 6, background: "white", color: "#1a1a1a", fontFamily: "monospace",
              }}
              onFocus={(e) => e.currentTarget.select()}
            />
            <button
              onClick={() => copyLink(`just_${justInvited.email}`, justInvited.inviteLink)}
              style={{ ...btnSecondary, fontSize: 12, padding: "8px 14px", whiteSpace: "nowrap" }}
            >
              {copiedId === `just_${justInvited.email}` ? "コピー済み✓" : "リンクをコピー"}
            </button>
            <button
              onClick={() => setJustInvited(null)}
              style={{ background: "none", border: "none", color: "#166534", fontSize: 18, cursor: "pointer", padding: "4px 8px" }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {invitations.length > 0 && (
        <div style={card}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 14 }}>招待中</div>
          {invitations.map((inv) => (
            <div key={inv.id} style={{ padding: "12px 0", borderTop: "1px solid #f0efeb", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                <div style={{ fontSize: 14, marginBottom: 2 }}>{inv.email}</div>
                <div style={{ fontSize: 12, color: "#6b6b68" }}>
                  {inv.role === "admin" ? "管理者" : "メンバー"}として招待中・期限: {new Date(inv.expiresAt).toLocaleDateString("ja-JP")}
                </div>
              </div>
              <button
                onClick={() => copyLink(inv.id, inv.inviteLink)}
                style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}
              >
                {copiedId === inv.id ? "コピー済み✓" : "リンクをコピー"}
              </button>
              <button onClick={() => handleCancelInvite(inv.id)} style={{ ...btnSecondary, fontSize: 12, padding: "6px 14px" }}>
                取り消し
              </button>
            </div>
          ))}
        </div>
      )}

      <div style={{ ...card, borderLeft: "3px solid #534AB7", borderRadius: "0 12px 12px 0" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#534AB7", marginBottom: 8 }}>ロール権限</div>
        <ul style={{ fontSize: 14, color: "#6b6b68", lineHeight: 2, paddingLeft: 18, margin: 0 }}>
          <li><strong>オーナー</strong>: 全操作・課金設定・他のオーナー設定</li>
          <li><strong>管理者</strong>: ユーザー招待・接続設定・送信</li>
          <li><strong>メンバー</strong>: 送信・送信ログ閲覧</li>
        </ul>
      </div>
    </>
  );
}
